import { Alert, Button, Card, Form, Input, Select, Space, message } from "antd";
import type { SelectProps } from "antd/lib/select";

const { TextArea } = Input;

type BulkSmsPanelProps = {
  layout: object;
  tailLayout: object;
  loading: boolean;
  messageTypeOptions: SelectProps["options"];
  smsEnabled: boolean;
  hasSmsCredentials: boolean;
  onNavigateToCredentials?: () => void;
  onNavigateToSmsTemplates?: () => void;
  onSend: (values: any) => void;
};

const BulkSmsPanel: React.FC<BulkSmsPanelProps> = ({
  layout,
  tailLayout,
  loading,
  messageTypeOptions,
  smsEnabled,
  hasSmsCredentials,
  onNavigateToCredentials,
  onNavigateToSmsTemplates,
  onSend,
}) => {
  const canSend = smsEnabled && hasSmsCredentials;

  const handleFinish = (values: any) => {
    if (!smsEnabled) {
      message.error("Enable SMS under SMS Templates before sending bulk SMS.");
      return;
    }
    if (!hasSmsCredentials) {
      message.error(
        "SMS credentials are empty. Fill them in under SMS Credentials first."
      );
      return;
    }
    onSend(values);
  };

  return (
    <Card title="Bulk SMS" bordered hoverable>
      {!smsEnabled && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="SMS is not enabled"
          description="Turn on SMS under SMS Templates before you can send bulk SMS."
          action={
            onNavigateToSmsTemplates ? (
              <Button size="small" onClick={onNavigateToSmsTemplates}>
                SMS Templates
              </Button>
            ) : undefined
          }
        />
      )}

      {smsEnabled && !hasSmsCredentials && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="SMS credentials must be filled in first"
          description="Enter the SMS API key and secret under SMS Credentials before sending bulk SMS."
          action={
            onNavigateToCredentials ? (
              <Button size="small" type="primary" onClick={onNavigateToCredentials}>
                Go to SMS Credentials
              </Button>
            ) : undefined
          }
        />
      )}

      <Form {...layout} onFinish={handleFinish}>
        <div className="card-content">
          <Form.Item
            name="MessageType"
            label="Message Type"
            rules={[{ required: true }]}
          >
            <Select
              id="MessageType"
              options={messageTypeOptions}
              size="large"
              style={{ width: "100%" }}
              placeholder="Message Type"
              disabled={!canSend}
            />
          </Form.Item>
        </div>
        <div className="card-content">
          <Form.Item
            name="CustomMessage"
            label="Custom Message"
            rules={[{ required: true }]}
          >
            <TextArea
              size="large"
              placeholder="Custom Message"
              showCount
              maxLength={160}
              disabled={!canSend}
            />
          </Form.Item>
        </div>
        <div className="card-content">
          <Form.Item {...tailLayout}>
            <Space direction="vertical" style={{ width: "100%" }}>
              {canSend ? (
                <Button
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  htmlType="submit"
                  loading={loading}
                >
                  Send
                </Button>
              ) : (
                <Button
                  style={{ marginBottom: 10 }}
                  block
                  size="large"
                  danger
                  onClick={() => {
                    if (!smsEnabled) {
                      message.error(
                        "Enable SMS under SMS Templates before sending bulk SMS."
                      );
                      return;
                    }
                    message.error(
                      "SMS credentials are empty. Fill them in under SMS Credentials first."
                    );
                    onNavigateToCredentials?.();
                  }}
                >
                  Send
                </Button>
              )}
            </Space>
          </Form.Item>
        </div>
      </Form>
    </Card>
  );
};

export default BulkSmsPanel;
